import { customInstance } from "@repo/api-client";

/**
 * Client contract for the operator back-office REST surface (`/api/v1/ops/...`, super-admin.md §C.13).
 * Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), NOT a raw fetch —
 * a 409 stale override (OP-4) comes back as an `ApiError` whose problem body embeds the authoritative
 * current record for the re-confirm loop. The Orval client is regenerated from the backend OpenAPI at
 * serial integration (`just gen`); these thin accessors are the stand-in, matching `guestsApi`.
 */

/** Aggregate first-booking funnel; definitions in docs/business/metrics.md. */
export interface FirstBookingFunnel {
  cohortVenues: number;
  convertedVenues: number;
  /** 0..1; null until a venue has been live for 7 days. */
  convertedShare: number | null;
  medianSignupToFirstBookingSeconds: number | null;
  medianLiveToFirstBookingSeconds: number | null;
  firstBookingsByChannel: Record<string, number>;
}

export interface TenantSearchHit {
  tenantId: string;
  accountName: string;
  accountStatus: string;
  venueId: string;
  venueDisplayName: string;
  slug: string;
  lifecycle: string;
}

export interface VenueHealth {
  venueId: string;
  displayName: string;
  slug: string;
  lifecycle: string;
  version: number;
  bookingsTotal: number;
  openDsrCount: number;
  subscriptionState: string | null;
  trialState: string | null;
  entitlementSummary: string | null;
}

export interface TenantHealth {
  tenantId: string;
  accountName: string;
  accountStatus: string;
  venues: VenueHealth[];
}

export interface VenueState {
  venueId: string;
  tenantId: string;
  slug: string;
  displayName: string;
  lifecycle: string;
  version: number;
}

export interface ImpersonationSession {
  id: string;
  operatorId: string;
  tenantId: string;
  venueId: string;
  state: string;
  endReason: string | null;
  startedAt: string;
  maxDurationSeconds: number;
  idleTimeoutSeconds: number;
  lastActivityAt: string;
}

export interface OperatorAuditEntry {
  id: string;
  operatorId: string;
  action: string;
  tenantId: string | null;
  venueId: string | null;
  targetRef: string | null;
  before: string | null;
  after: string | null;
  reason: string | null;
  impersonationSessionId: string | null;
  at: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface EntitlementFlag {
  key: string;
  enabled: boolean;
  source: string; // TRIAL | PLAN | OPERATOR_OVERRIDE
  since: string;
  overrideExpiresAt: string | null;
}

export interface EntitlementView {
  venueId: string;
  flags: EntitlementFlag[];
  version: number;
}

const OPS = "/api/v1/ops";

export const getFirstBookingFunnel = (): Promise<FirstBookingFunnel> =>
  customInstance<FirstBookingFunnel>(`${OPS}/first-booking-funnel`, { method: "GET" });

export const searchTenants = (query: string): Promise<TenantSearchHit[]> =>
  customInstance<TenantSearchHit[]>(`${OPS}/search?q=${encodeURIComponent(query)}`, {
    method: "GET",
  });

/** Default listing shown with an empty search box: every establishment, alphabetical, keyset-paginated. */
export const listTenants = (cursor?: string): Promise<Page<TenantSearchHit>> =>
  customInstance<Page<TenantSearchHit>>(
    `${OPS}/tenants?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    { method: "GET" },
  );

export const tenantHealth = (tenantId: string): Promise<TenantHealth> =>
  customInstance<TenantHealth>(`${OPS}/tenants/${encodeURIComponent(tenantId)}/health`, {
    method: "GET",
  });

export const searchAudit = (tenantId: string, cursor?: string): Promise<Page<OperatorAuditEntry>> =>
  customInstance<Page<OperatorAuditEntry>>(
    `${OPS}/audit?tenantId=${encodeURIComponent(tenantId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    { method: "GET" },
  );

export const suspendVenue = (
  venueId: string,
  reason: string,
  expectedVersion: number,
): Promise<VenueState> =>
  customInstance<VenueState>(`${OPS}/venues/${encodeURIComponent(venueId)}/suspend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, expectedVersion }),
  });

export const reactivateVenue = (
  venueId: string,
  reason: string,
  expectedVersion: number,
): Promise<VenueState> =>
  customInstance<VenueState>(`${OPS}/venues/${encodeURIComponent(venueId)}/reactivate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, expectedVersion }),
  });

export const startImpersonation = (
  venueId: string,
  reason: string,
): Promise<ImpersonationSession> =>
  customInstance<ImpersonationSession>(`${OPS}/venues/${encodeURIComponent(venueId)}/impersonate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });

export const endImpersonation = (sessionId: string): Promise<{ ended: boolean }> =>
  customInstance<{ ended: boolean }>(`${OPS}/impersonation/${encodeURIComponent(sessionId)}/end`, {
    method: "POST",
  });

export const activeImpersonations = (venueId: string): Promise<ImpersonationSession[]> =>
  customInstance<ImpersonationSession[]>(
    `${OPS}/venues/${encodeURIComponent(venueId)}/impersonations`,
    {
      method: "GET",
    },
  );

// --- per-venue entitlements (feature flags, §C.3 — operator view + override) ----------------------

const entBase = (venueId: string) => `${OPS}/venues/${encodeURIComponent(venueId)}/entitlements`;

export const venueEntitlements = (venueId: string): Promise<EntitlementView> =>
  customInstance<EntitlementView>(entBase(venueId), { method: "GET" });

/** Set an operator override on one flag (wins over plan/trial). `enabled=false` explicitly revokes. */
export const setEntitlementOverride = (
  venueId: string,
  flag: string,
  enabled: boolean,
  reason: string,
  expiresAt: string, // ISO instant, must be future
): Promise<EntitlementView> =>
  customInstance<EntitlementView>(`${entBase(venueId)}/override`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ flag, enabled, reason, expiresAt }),
  });

/** Clear a flag's override, reverting it to its trial/plan source. */
export const clearEntitlementOverride = (venueId: string, flag: string): Promise<EntitlementView> =>
  customInstance<EntitlementView>(`${entBase(venueId)}/override/${encodeURIComponent(flag)}`, {
    method: "DELETE",
  });

// --- sales trials (sales-trials.md, R1 — operator-only) --------------------------------------------

export type TrialState = "REQUESTED" | "ACTIVE" | "CONVERTED" | "EXPIRED" | "ENDED_EARLY";

export interface TrialView {
  trialId: string;
  state: TrialState;
  grantedFlags: string[];
  requestedAtSec: number;
  activationDueAtSec: number;
  activatedAtSec: number | null;
  endsAtSec: number;
  remainingSeconds: number;
  version: number;
}

export interface Trial {
  id: string;
  tenantId: string;
  state: TrialState;
  grantedFlags: string[];
  requestedAtSec: number;
  activationDueAtSec: number;
  activatedAtSec: number | null;
  endsAtSec: number;
  endedAtSec: number | null;
  endReason: string | null;
  convertedAtSec: number | null;
  version: number;
}

const trialBase = (tenantId: string) => `${OPS}/tenants/${encodeURIComponent(tenantId)}/trials`;

export const activeTrial = (tenantId: string): Promise<TrialView | null> =>
  customInstance<TrialView | null>(`${trialBase(tenantId)}/active`, { method: "GET" });

export const requestTrial = (
  tenantId: string,
  grantedFlags: string[],
  durationDays: number,
  reason: string,
): Promise<Trial> =>
  customInstance<Trial>(trialBase(tenantId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grantedFlags, durationDays, reason }),
  });

export const activateTrial = (
  tenantId: string,
  trialId: string,
  reason: string,
  expectedVersion: number,
): Promise<Trial> =>
  customInstance<Trial>(`${trialBase(tenantId)}/${encodeURIComponent(trialId)}/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, expectedVersion }),
  });

export const extendTrial = (
  tenantId: string,
  trialId: string,
  newEndsAtSec: number,
  reason: string,
  expectedVersion: number,
  grantedFlags?: string[],
): Promise<Trial> =>
  customInstance<Trial>(`${trialBase(tenantId)}/${encodeURIComponent(trialId)}/extend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newEndsAtSec, reason, expectedVersion, grantedFlags }),
  });

export const adjustTrialGrant = (
  tenantId: string,
  trialId: string,
  grantedFlags: string[],
  reason: string,
  expectedVersion: number,
): Promise<Trial> =>
  customInstance<Trial>(`${trialBase(tenantId)}/${encodeURIComponent(trialId)}/adjust-grant`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grantedFlags, reason, expectedVersion }),
  });

export const endTrialEarly = (
  tenantId: string,
  trialId: string,
  reason: string,
  expectedVersion: number,
): Promise<Trial> =>
  customInstance<Trial>(`${trialBase(tenantId)}/${encodeURIComponent(trialId)}/end-early`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, expectedVersion }),
  });
